/**
 * @fileoverview Boot-time hydration of the favorite stores from the server.
 *
 * Server-side favorites are the source of truth; the two localStorage stores
 * (`favorite-cases.ts`, `favorite-apps.ts`) are an optimistic/offline
 * first-paint cache. After the auth token resolves, `hydrateFavoritesFromServer`
 * pulls the authenticated user's favorites once and seeds each store with its
 * resource-type subset — replacing (and refreshing) the localStorage cache.
 *
 * Dependency direction is one-way to avoid a cycle: this module imports each
 * store's `seedFromServer`; the stores never import this module.
 */

import { api } from "./api";
import { seedFromServer as seedCases } from "./favorite-cases";
import { seedFromServer as seedApps } from "./favorite-apps";

let hydrated = false;

/**
 * Pull the signed-in user's server favorites once and seed both stores. Safe to
 * call multiple times (idempotent via the `hydrated` guard). On any failure the
 * localStorage-seeded state is left intact (offline tolerance).
 */
export async function hydrateFavoritesFromServer(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  try {
    const { favorites } = await api.listFavorites();
    const caseIds: string[] = [];
    const appIds: string[] = [];
    for (const f of favorites) {
      if (f.resourceType === "case") caseIds.push(f.resourceId);
      else if (f.resourceType === "app") appIds.push(f.resourceId);
    }
    seedCases(caseIds);
    seedApps(appIds);
  } catch {
    // Offline / unauthenticated / transient API failure: keep the
    // localStorage-seeded state so favorites still render from the cache.
    hydrated = false;
  }
}
